import { useMemo } from 'react';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import {
  joinFiltersWithAnd,
  joinFiltersWithOr,
} from '@sorare/core/src/lib/algolia';

import { usePlayersOwnedByCurrentUser } from 'hooks/search/usePlayersOwnedByCurrentUser';
import useDefaultFilters from 'hooks/useDefaultFilters';

type Props = {
  rarity: Rarity;
  isNationalTeam?: boolean;
  teamSlugs?: string[];
  playerSlugs?: string[];
};
export const usePrimaryBuyAlgoliaParams = ({
  isNationalTeam = false,
  teamSlugs = [],
  playerSlugs = [],
  rarity,
}: Props) => {
  const { sport = Sport.FOOTBALL } = useSportContext();
  const defaultFilters = useDefaultFilters({
    primaryOffer: true,
    bundled: false,
    rarity,
  });

  const { playerDisplayNames: excludedPlayersDisplayNames, loading } =
    usePlayersOwnedByCurrentUser();

  const teamsFilters = useMemo(() => {
    if (isNationalTeam) {
      return joinFiltersWithOr(
        teamSlugs.map(teamSlug => `active_national_team.slug:"${teamSlug}"`)
      );
    }
    return joinFiltersWithOr(
      teamSlugs.map(teamSlug => `team.slug:"${teamSlug}"`)
    );
  }, [isNationalTeam, teamSlugs]);
  const playerFilters = useMemo(
    () =>
      joinFiltersWithOr(
        playerSlugs.map(playerSlug => `player.slug:"${playerSlug}"`)
      ),
    [playerSlugs]
  );

  const excludePlayersFilters = useMemo(
    () =>
      joinFiltersWithAnd(
        excludedPlayersDisplayNames.map(
          playerDisplayName => `NOT player.display_name:"${playerDisplayName}"`
        )
      ),
    [excludedPlayersDisplayNames]
  );

  const facetFilters = useMemo(
    () =>
      joinFiltersWithAnd([
        ...defaultFilters,
        `sport:${sport}`,
        teamsFilters,
        playerFilters,
        excludePlayersFilters,
      ]),
    [defaultFilters, sport, teamsFilters, playerFilters, excludePlayersFilters]
  );

  const indexes = ['Highest Average Score'];

  return {
    indexes,
    facetFilters,
    sport,
    loading,
  };
};
